<script setup lang="ts">
  import { ref, watch, onMounted, onUnmounted } from "vue";
  import { Button, IconArrowLeft } from "@ui-kit";
  import { useI18n } from "vue-i18n";
  import { useSearchUHF, usePowerRfid } from "@plugins";
  import { Widget, StatusBar, Image } from "@src/components";
  import { useWidgetsStore } from "@src/store";
  import { pairEpcApi, ResponseCode } from "@src/api";
  import { useProfileOptions } from "@src/hooks";
  import { default as SearchUHFPowerOnIcon } from "./SearchUHFPowerOnIcon.vue";

  const widgets_store = useWidgetsStore();
  const search_uhf = useSearchUHF();
  const power_rfid = usePowerRfid();
  const profile_options = useProfileOptions();
  const { t } = useI18n();

  const stage = ref<number | null>(null);
  const incoming_value = ref(0);
  const decrease_value_buffer = {
    pool: [] as number[],
    date: null as number | null,
  };

  const status_info = {
    1: {
      icon: "#D09607",
      background: "#F5B108",
      title: t("Товар недалеко"),
      description: t("Продолжайте двигаться в этом направлении"),
    },
    2: {
      icon: "#AB9905",
      background: "#C9B406",
      title: t("Товар недалеко"),
      description: t("Продолжайте двигаться в этом направлении"),
    },
    3: {
      icon: "#869B04",
      background: "#9EB704",
      title: t("Товар близко"),
      description: t("Осталось совсем немного, товар где-то рядом с вами"),
    },
    4: {
      icon: "#619D02",
      background: "#73B902",
      title: t("Товар близко"),
      description: t("Осталось совсем немного, товар где-то рядом с вами"),
    },
    5: {
      icon: "#3CA000",
      background: "#47BC00",
      title: t("Товар найден") + "!",
      description: t("Он должен быть прямо перед вами"),
    },
  };

  const getTitle = () => {
    if (!search_uhf.state.search_is_running) return t("Локатор");

    if (stage.value > 0) {
      return status_info[stage.value].title;
    }

    return t("Поиск товара") + "...";
  };

  const getDescription = () => {
    if (!search_uhf.state.search_is_running) return t("Нажмите курок для начала сканирования");

    if (stage.value > 0) {
      return status_info[stage.value].description;
    }

    return t("Пробуйте водить устройством в разных направлениях");
  };

  watch(
    () => search_uhf.state.search_is_running,
    (newValue) => {
      if (!newValue) {
        stage.value = null;
        incoming_value.value = 0;
      }
    },
  );

  onMounted(async () => {
    await power_rfid.set(profile_options.profile.value.constants.rfid.power_locator || 20);
    await search_uhf.subscribeToPluginEvent();

    const barcode = widgets_store.search_uhf.item.barcode.filter((barcode) => !barcode.startsWith("2"))[0];
    const result = await pairEpcApi({ barcode });

    if (result.code === ResponseCode.OK) {
      search_uhf.setFilterForSearch(result.epc.slice(0, 15));
      search_uhf.enableBroadcast();
      search_uhf.subscribeToDeviceKeyEvents();
      search_uhf.subscribeToSearchUHF(({ value }) => {
        const apply = (new_state: number) => {
          decrease_value_buffer.pool = [];
          decrease_value_buffer.date = Date.now();
          incoming_value.value = new_state;

          const _stage = Math.min(Math.floor(new_state / 20) + 1, 5);

          if (_stage !== stage.value) {
            stage.value = _stage;
          }
        };

        if (value <= 0) {
          decrease_value_buffer.pool = [];
          stage.value = null;

          return;
        }

        if (value > incoming_value.value) {
          apply(value);

          return;
        }

        if (value < incoming_value.value) {
          decrease_value_buffer.pool.push(value);

          if (Date.now() - decrease_value_buffer.date > 500) {
            apply(Math.max(...decrease_value_buffer.pool));
          }
        }
      });
    }
  });

  onUnmounted(async () => {
    const locator_power = profile_options.getRfidConstantValue("power_locator", 20);

    if (locator_power !== null) {
      await power_rfid.set(power_rfid.prevPower.value);
    }

    search_uhf.unsubscribeToSearchUHF();
    search_uhf.unsubscribeToDeviceKeyEvents();
    search_uhf.unsubscribeToPluginEvent();
  });
</script>

<template>
  <Widget>
    <div class="search-uhf" :class="{ 'search-uhf--active': search_uhf.state.search_is_running }">
      <div
        v-if="stage !== null"
        class="search-uhf__status-finding"
        :style="{
          height: stage ? `${180 * stage}px` : 0,
          backgroundColor: stage ? status_info[stage].background : 'transparent',
        }"
      >
        <div
          class="search-uhf__status-finding__text"
          :style="{ backgroundColor: stage ? status_info[stage].icon : 'transparent', position: 'relative' }"
        >
          {{ incoming_value }}
        </div>
      </div>
      <div
        v-else
        class="search-uhf__power-on"
        :class="{ 'search-uhf__power-on--active': search_uhf.state.search_is_running }"
      >
        <SearchUHFPowerOnIcon />
      </div>
      <StatusBar />
      <div class="search-uhf__content">
        <div class="search-uhf__back-wrapper">
          <Button as-square @click="() => widgets_store.close('search_uhf')">
            <template #iconBefore>
              <IconArrowLeft />
            </template>
          </Button>
        </div>
        <div class="search-uhf__info">
          <span class="search-uhf__title">{{ getTitle() }}</span>
          <span class="search-uhf__description">{{ getDescription() }}</span>
        </div>
        <div class="search-uhf__footer">
          <div class="search-uhf__item">
            <Image :url="widgets_store.search_uhf.item.image_url" />
            <div class="search-uhf__item__info">
              <div class="search-uhf__item__description">
                <span class="search-uhf__item__name">{{ widgets_store.search_uhf.item.title }}</span>
                <span class="search-uhf__item__art-col">
                  {{ `${widgets_store.search_uhf.item.article}, ${widgets_store.search_uhf.item.color}` }}
                </span>
              </div>
              <div class="search-uhf__item__size-wrapper">
                <span class="search-uhf__item__size">{{ widgets_store.search_uhf.item.size }}</span>
              </div>
            </div>
          </div>
          <button class="search-uhf__close-btn" @click="() => widgets_store.close('search_uhf')">
            {{ $t("Закрыть") }}
          </button>
        </div>
      </div>
    </div>
  </Widget>
</template>

<style scoped>
  .search-uhf {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--sf-main-990);
    position: relative;

    & :deep(.statusbar) {
      position: relative;
    }
  }

  .search-uhf--active {
    background-color: var(--sf-main-700);
  }

  .search-uhf__status-finding {
    width: 600px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    transition:
      height 0.3s ease-in-out,
      background-color 0.3s ease-in-out;
  }

  .search-uhf__status-finding__text {
    width: 160px;
    height: 160px;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.3s ease-in-out;
    color: #ffffff;
    font-size: 64px;
    font-weight: 600;
  }

  .search-uhf__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 20px;
    padding: 8px 16px 16px;
    position: relative;
  }

  .search-uhf__back-wrapper {
    & :deep(.button) {
      background-color: #575757b2;
      box-shadow:
        0px 4px 24px 0px #00000033,
        0px 8px 32px 0px #0000003d,
        1px 1px 0px 0px #ffffff4d inset,
        -1px -1px 0px 0px #ffffff1a inset;
      backdrop-filter: blur(12px);
    }

    & :deep(.button .button__content .button__inner) {
      font-weight: 580;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: 0.16px;
      color: #ffffff;
    }

    & :deep(.icon) {
      stroke: #ffffff;
    }
  }

  .search-uhf__info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .search-uhf__title {
    font-weight: 500;
    font-size: 32px;
    line-height: 40px;
    color: #ffffff;
  }

  .search-uhf__description {
    font-weight: 580;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.36px;
    color: #ffffff;
  }

  .search-uhf__power-on {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    width: 100px;
    height: 100px;
    background-color: var(--sf-main-900);
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .search-uhf__power-on--active {
    width: 160px;
    height: 160px;
    background-color: var(--sf-main-500);

    & :deep(svg) {
      width: 80px;
      height: 80px;

      & path {
        fill: var(--sf-main-0);
      }
    }
  }

  .search-uhf__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: auto;
  }

  .search-uhf__item {
    display: flex;
    gap: 8px;
    padding: 12px;
    border-radius: 16px;
    background-color: var(--sf-base-background-tabbar);
    box-shadow:
      0px 4px 24px 0px #00000033,
      0px 8px 32px 0px #0000003d,
      1px 1px 0px 0px #ffffff4d inset,
      -1px -1px 0px 0px #ffffff1a inset;
    background: #575757b2;

    & :deep(.image) {
      width: 52px;
      height: 60px;
    }
  }

  .search-uhf__item__info {
    display: flex;
    gap: 8px;
    flex: 1;
    align-items: center;
    justify-content: center;
  }

  .search-uhf__item__description {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .search-uhf__item__name {
    font-weight: 580;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.16px;
    color: #ffffff;
  }

  .search-uhf__item__art-col {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.14px;
    color: #b3b3b3;
  }

  .search-uhf__item__size-wrapper {
    display: flex;
    padding: 6px 10px;
    border-radius: 8px;
    background-color: #2c2c2c;
  }

  .search-uhf__item__size {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.14px;
    color: #ffffff;
  }

  .search-uhf__close-btn {
    width: 100%;
    background-color: #575757b2;
    border-radius: 12px;
    height: 48px;
    box-shadow:
      0px 4px 24px 0px #00000033,
      0px 8px 32px 0px #0000003d,
      1px 1px 0px 0px #ffffff4d inset,
      -1px -1px 0px 0px #ffffff1a inset;
    border: none;
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    box-sizing: border-box;
    overflow: hidden;
    color: #ffffff;
  }
</style>
